import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

const BANDERAS = [
  { codigo:'co', pais:'Colombia' },
  { codigo:'us', pais:'United States' },
  { codigo:'gb', pais:'United Kingdom' },
  { codigo:'ca', pais:'Canada' },
]

function Bandera({ codigo, pais, size = 18 }) {
  return (
    <img
      src={'https://flagcdn.com/24x18/' + codigo + '.png'}
      alt={pais}
      title={pais}
      style={{ width: size, height: Math.round(size * 0.75), borderRadius: 3, objectFit:'cover', boxShadow:'0 1px 3px rgba(0,0,0,0.4)', flexShrink:0 }}
    />
  )
}

/* Icono de ojo (mostrar/ocultar contrasena) -- SVG simple, sin dependencias */
function IconoOjo({ abierto, size = 18 }) {
  return abierto ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.3 20.3 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a20.3 20.3 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

function LogoEduNova({ size = 32 }) {
  return (
    <img src="/edunova-logo.png" alt="EduNova"
      style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }} />
  )
}

/* Logo propio de EnglishBridge (junto al nombre en la pantalla de login) */
function LogoEnglishBridge({ size = 32 }) {
  return (
    <img src="/englishbridge-logo.png" alt="EnglishBridge"
      style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }} />
  )
}

function Wordmark({ size = 20 }) {
  return (
    <div style={{ display:'flex', alignItems:'baseline', fontWeight:900, fontSize:size, letterSpacing:'-0.5px' }}>
      <span style={{ color:'#F0D878' }}>English</span>
      <span style={{ color:'#fff' }}>Bridge</span>
    </div>
  )
}

/* Hook simple para detectar si estamos en tamano movil */
function useEsMovil() {
  const [esMovil, setEsMovil] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 820 : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)')
    const handler = e => setEsMovil(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return esMovil
}

/*
  NOTA (mismo patron de seguridad que EspanolGo):
  La opcion "Admin" solo aparece si la URL trae ?admin=1
  Ej: tusitio.com/?admin=1
*/
const PARAM_ADMIN_SECRETO = 'admin'

const API = process.env.REACT_APP_API_URL

export default function Login({ onLogin, planElegido, datosRevision, onVolver, soloEstudiante }) {
  // Si viene de elegir un plan en Precios.jsx, o del boton "Get Started" del
  // landing (prop soloEstudiante -- ese boton es solo para estudiantes
  // nuevos), nos saltamos la pregunta de "Estudiante/Profesor" (siempre es
  // un estudiante) y vamos derecho al formulario de correo/contrasena. El
  // selector de rol solo se muestra cuando entra directo a iniciar sesion
  // sin haber elegido un plan antes. Dentro de la app instalada
  // (Android/iOS) pasa lo mismo siempre: el profesor da clase desde
  // laptop/PC, nunca desde la app movil, asi que ahi ni siquiera se le
  // muestra la opcion de rol.
  const esApp = Capacitor.isNativePlatform()
  const soloRolEstudiante = esApp || soloEstudiante
  const [rol,      setRol]      = useState(() => (planElegido || soloRolEstudiante) ? 'estudiante' : null)
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [mostrarPassword, setMostrarPassword] = useState(false)
  const [error,    setError]    = useState('')
  const [carga,    setCarga]    = useState(false)
  // Para estudiantes, por defecto asumimos que es alguien nuevo (viene de
  // elegir un plan) y solo se le muestra "Crear cuenta". Si ya tenia
  // cuenta (por ejemplo, ya hizo la prueba gratis y ahora vuelve a pagar),
  // toca el enlace de abajo para que le aparezca el boton de Ingresar. Una
  // vez el navegador ya confirmo un correo antes (ver EmailConfirmado.jsx),
  // se recuerda con localStorage y arranca directo en modo "Ingresar".
  const [modoExistente, setModoExistente] = useState(() => {
    try {
      return window.localStorage.getItem('englishbridge_email_confirmado') === '1'
    } catch (err) {
      return false
    }
  })
  const esMovil = useEsMovil()

  const [adminOculto] = useState(() => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return params.get(PARAM_ADMIN_SECRETO) === '1'
  })

  async function handleSubmit(e) {
    e.preventDefault()
    setCarga(true); setError('')
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) { setError(error.message); setCarga(false); return }
      onLogin(data.user, rol)
    } catch (err) {
      setError('Error de conexion. Intenta de nuevo.')
      setCarga(false)
    }
  }

  async function handleRegistro() {
    if (!email || !password) { setError('Ingresa email y contrasena'); return }
    setCarga(true); setError('')

    const redirectUrl = window.location.origin + '/email-confirmado'

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl }
    })

    if (error) { setError(error.message); setCarga(false); return }

    // Si eligio el plan Free, le arrancamos ya la prueba gratis de 3 dias
    // (esto tambien le avisa por correo al profesor de que hay un
    // estudiante nuevo). Si eligio un plan pago, esto no aplica — pagara
    // en la pantalla de Checkout justo despues de este registro.
    if (planElegido?.esFree) {
      try {
        await axios.post(API + '/pagos/iniciar-prueba', { usuario_id: data.user.id, email })
      } catch (err) {
        console.error('Error iniciando prueba gratis:', err)
      }
    } else if (planElegido) {
      try {
        await axios.post(API + '/pagos/notificar-registro', { email, plan_nombre: planElegido.nombre })
      } catch (err) {
        console.error('Error notificando registro:', err)
      }
    }

    // Si venia de un examen de nivel, enviamos escritura + grabaciones al equipo docente
    if (datosRevision && (datosRevision.escritura?.length || datosRevision.grabaciones?.length)) {
      try {
        const form = new FormData()
        form.append('email', email)
        form.append('usuario_id', data.user.id)
        form.append('escritura', JSON.stringify(
          (datosRevision.escritura || []).map(e => ({ pregunta: e.pregunta, nivel: e.nivel, texto: e.texto }))
        ))
        form.append('temas_speaking', JSON.stringify(
          (datosRevision.grabaciones || []).map(g => ({ pregunta_id: g.pregunta_id, nivel: g.nivel, tema: g.tema, duracion: g.duracion }))
        ))
        ;(datosRevision.grabaciones || []).forEach(g => {
          if (g.blob) form.append('audios', g.blob, g.pregunta_id + '_' + g.nivel + '.webm')
        })

        await axios.post(API + '/examen-revision/enviar', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } catch (err) {
        console.error('Error enviando examen a revision:', err)
      }
    }

    // Si el proyecto de Supabase tiene desactivada la confirmacion por
    // correo, signUp ya devuelve una sesion activa: seguimos de una vez
    // (a Checkout si eligio un plan pago, o directo a la plataforma si
    // era Free). Si Supabase SI exige confirmar el correo primero, no hay
    // sesion todavia y hay que esperar a que el estudiante confirme.
    if (data.session) {
      onLogin(data.user, rol)
      return
    }

    setError(
      planElegido && !planElegido.esFree && planElegido.precio > 0
        ? 'Revisa tu email para confirmar tu cuenta. Cuando la confirmes, vuelve a entrar con el mismo correo para completar el pago del plan ' + planElegido.nombre + '.'
        : 'Revisa tu email para confirmar tu cuenta. Un profesor revisara tu examen pronto.'
    )
    setCarga(false)
  }

  // Dentro de la app instalada, o al entrar por "Get Started" (solo
  // estudiantes nuevos), solo Estudiante — el profesor da clase desde
  // laptop/PC (navegador), nunca desde la app movil ni desde ese boton.
  const ROLES = soloRolEstudiante
    ? [
        { id:'estudiante', ic:'🎓', label:'Estudiante', desc:'Accede a tus clases, examenes y retos', color:'#1B3A8C' },
      ]
    : [
        { id:'estudiante', ic:'🎓', label:'Estudiante', desc:'Accede a tus clases, examenes y retos', color:'#1B3A8C' },
        { id:'profesor',   ic:'👨‍🏫', label:'Profesor',   desc:'Gestiona tus clases y estudiantes',   color:'#059669' },
      ]
  if (!soloRolEstudiante && adminOculto) {
    ROLES.push({ id:'admin', ic:'⚙️', label:'Admin', desc:'Panel de administracion completo', color:'#EF4444' })
  }

  return (
    <div style={{ minHeight:'100vh', display: esMovil ? 'block' : 'flex', position:'relative', overflow:'hidden', fontFamily:'system-ui, sans-serif' }}>

      {/* Fondo */}
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(145deg, #0D1F52 0%, #1B3A8C 55%, #0a1836 100%)', zIndex:0 }} />
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', zIndex:1, opacity:0.08 }} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="gridLogin" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M50 0H0V50" fill="none" stroke="#F0D878" strokeWidth="0.6" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#gridLogin)"/>
      </svg>
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(90deg, rgba(10,24,54,0.5) 0%, rgba(10,24,54,0.25) 60%, rgba(10,24,54,0.85) 100%)', zIndex:2 }} />

      {/* Panel izquierdo (branding) */}
      <div style={{ flex: esMovil ? 'none' : 1, display:'flex', alignItems:'center', justifyContent:'center', padding: esMovil ? '40px 24px 24px' : '48px', position:'relative', zIndex:4 }}>
        <div style={{ color:'#fff', maxWidth:'440px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom: esMovil ? 16 : 24, flexWrap:'wrap' }}>
            {BANDERAS.map((b,i) => <Bandera key={i} codigo={b.codigo} pais={b.pais} size={esMovil ? 16 : 20} />)}
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: esMovil ? 14 : 22 }}>
            <LogoEnglishBridge size={esMovil ? 36 : 46} />
            <Wordmark size={esMovil ? 26 : 34} />
          </div>

          {!esMovil && (
            <div style={{ display:'flex', flexDirection:'column', gap:'11px', marginBottom:'32px' }}>
              {[
                { ic:'💻', text:'Live virtual classes with certified teachers' },
                { ic:'🏫', text:'In-person classes available'                  },
                { ic:'🎮', text:'Games, XP and daily challenges'               },
                { ic:'🏆', text:'Structured curriculum, A1 to C2'              },
              ].map((item, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <span style={{ fontSize:'17px' }}>{item.ic}</span>
                  <span style={{ fontSize:'14px', opacity:.88 }}>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display:'flex', alignItems:'center', gap:'10px', opacity:.7 }}>
            <div style={{ width:'34px', height:'34px', borderRadius:'8px', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <img src="/edunova-logo.png" alt="EduNova" style={{ width:28, height:28, objectFit:'contain' }} />
            </div>
            <span style={{ fontSize:'11px', letterSpacing:'1.2px', textTransform:'uppercase' }}>by EduNova Corporation</span>
          </div>
        </div>
      </div>

      {/* Panel derecho (formulario) */}
      <div style={{ width: esMovil ? '100%' : '460px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', padding: esMovil ? '32px 20px 48px' : '40px 48px', position:'relative', zIndex:4, overflowY:'auto', boxSizing:'border-box' }}>
        <div style={{ width:'100%' }}>

          {/* Este boton solo se muestra en la pantalla de seleccion de rol --
              una vez se elige un rol, el formulario de login mas abajo trae
              su propio boton "Volver" (con logica distinta). Mostrar los dos
              a la vez duplicaba el boton. */}
          {!rol && (
            <button onClick={onVolver}
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', fontFamily:'inherit', marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
              ← Volver
            </button>
          )}

          {planElegido && (
            <div style={{ background:'#EEF2FF', border:'1.5px solid #1B3A8C60', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:22 }}>{planElegido.emoji}</span>
              <div>
                <div style={{ fontSize:12, color:'#6B7280' }}>Plan seleccionado</div>
                <div style={{ fontSize:14, fontWeight:700, color:'#1A1A2E' }}>
                  {planElegido.nombre} — {planElegido.precio === 0 ? 'Gratis' : '$' + planElegido.precio.toLocaleString('es-CO') + ' COP'}{planElegido.periodo && planElegido.precio !== 0 ? ' ' + planElegido.periodo : ''}
                </div>
              </div>
            </div>
          )}

          {!rol ? (
            <div>
              <h2 style={{ fontSize: esMovil ? '21px' : '24px', fontWeight:800, marginBottom:6 }}>Bienvenido</h2>
              <p style={{ color:'#6B7280', marginBottom:24, fontSize:'14px' }}>Selecciona como quieres acceder</p>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {ROLES.map(r => (
                  <button key={r.id} onClick={() => setRol(r.id)}
                    style={{ display:'flex', alignItems:'center', gap:16, padding: esMovil ? '14px 16px' : '16px 20px', border:'2px solid #E2E6F0', borderRadius:14, background:'#fff', cursor:'pointer', textAlign:'left', width:'100%', fontFamily:'inherit', transition:'all .15s ease' }}
                    onMouseOver={e => { e.currentTarget.style.border='2px solid ' + r.color; e.currentTarget.style.background=r.color+'10' }}
                    onMouseOut={e => { e.currentTarget.style.border='2px solid #E2E6F0'; e.currentTarget.style.background='#fff' }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:r.color+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                      {r.ic}
                    </div>
                    <div>
                      <div style={{ fontSize:15, fontWeight:700, color:'#1A1A2E', marginBottom:2 }}>{r.label}</div>
                      <div style={{ fontSize:12, color:'#6B7280' }}>{r.desc}</div>
                    </div>
                    <div style={{ marginLeft:'auto', fontSize:18, color:'#9CA3AF' }}>→</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {/* Si vino de elegir un plan, esta dentro de la app, o entro por
                  "Get Started", nunca hubo selector de rol que mostrarle al
                  volver — lo mandamos a la pantalla anterior en su lugar. */}
              <button onClick={() => (planElegido || soloRolEstudiante) ? onVolver() : (setRol(null), setError(''))}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', fontFamily:'inherit', marginBottom:20, display:'flex', alignItems:'center', gap:6 }}>
                ← Volver
              </button>

              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:ROLES.find(r=>r.id===rol)?.color+'15', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                  {ROLES.find(r=>r.id===rol)?.ic}
                </div>
                <div>
                  <h2 style={{ fontSize: esMovil ? '19px' : '22px', fontWeight:800, marginBottom:2 }}>
                    Acceso {ROLES.find(r=>r.id===rol)?.label}
                  </h2>
                  <p style={{ color:'#6B7280', fontSize:'13px' }}>Ingresa tus credenciales</p>
                </div>
              </div>

              {error && (
                <div style={{ background:'#FEF2F2', color:'#991B1B', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px', border:'1px solid #FECACA' }}>
                  {error}
                </div>
              )}

              <form onSubmit={
                rol === 'estudiante' && !modoExistente
                  ? (e => { e.preventDefault(); handleRegistro() })
                  : handleSubmit
              }>
                <div style={{ marginBottom:'14px' }}>
                  <label style={{ display:'block', fontSize:'13px', fontWeight:600, marginBottom:'5px' }}>Email</label>
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #E2E6F0', borderRadius:'10px', fontFamily:'inherit', fontSize:'14px', outline:'none', boxSizing:'border-box' }}
                    placeholder="tu@email.com" />
                </div>
                <div style={{ marginBottom:'20px' }}>
                  <label style={{ display:'block', fontSize:'13px', fontWeight:600, marginBottom:'5px' }}>Password</label>
                  <div style={{ position:'relative' }}>
                    <input type={mostrarPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                      style={{ width:'100%', padding:'10px 42px 10px 14px', border:'1.5px solid #E2E6F0', borderRadius:'10px', fontFamily:'inherit', fontSize:'14px', outline:'none', boxSizing:'border-box' }}
                      placeholder="Minimo 6 caracteres" />
                    <button type="button" onClick={() => setMostrarPassword(v => !v)}
                      title={mostrarPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', padding:4, display:'flex', alignItems:'center' }}>
                      <IconoOjo abierto={mostrarPassword} />
                    </button>
                  </div>
                </div>

                {rol === 'estudiante' ? (
                  !modoExistente ? (
                    <>
                      <button type="submit" disabled={carga}
                        style={{ width:'100%', padding:'12px', background: ROLES.find(r=>r.id===rol)?.color || '#1B3A8C', color:'#fff', border:'none', borderRadius:'10px', fontFamily:'inherit', fontSize:'15px', fontWeight:700, cursor:'pointer', marginBottom:'10px' }}>
                        {carga ? 'Cargando...' : 'Crear cuenta'}
                      </button>
                      <button type="button" onClick={() => { setModoExistente(true); setError('') }}
                        style={{ width:'100%', padding:'8px', background:'none', border:'none', color:'#1B3A8C', fontFamily:'inherit', fontSize:'13px', fontWeight:600, cursor:'pointer', textDecoration:'underline' }}>
                        ¿Ya tienes cuenta? Inicia sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="submit" disabled={carga}
                        style={{ width:'100%', padding:'12px', background: ROLES.find(r=>r.id===rol)?.color || '#1B3A8C', color:'#fff', border:'none', borderRadius:'10px', fontFamily:'inherit', fontSize:'15px', fontWeight:700, cursor:'pointer', marginBottom:'10px' }}>
                        {carga ? 'Cargando...' : 'Ingresar'}
                      </button>
                      <button type="button" onClick={() => { setModoExistente(false); setError('') }}
                        style={{ width:'100%', padding:'8px', background:'none', border:'none', color:'#1B3A8C', fontFamily:'inherit', fontSize:'13px', fontWeight:600, cursor:'pointer', textDecoration:'underline' }}>
                        ¿Eres nuevo? Crea una cuenta
                      </button>
                    </>
                  )
                ) : (
                  <button type="submit" disabled={carga}
                    style={{ width:'100%', padding:'12px', background: ROLES.find(r=>r.id===rol)?.color || '#1B3A8C', color:'#fff', border:'none', borderRadius:'10px', fontFamily:'inherit', fontSize:'15px', fontWeight:700, cursor:'pointer', marginBottom:'10px' }}>
                    {carga ? 'Cargando...' : 'Ingresar como ' + ROLES.find(r=>r.id===rol)?.label}
                  </button>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}